/* eslint-disable no-console */
define((require, exports, module) => {
    'use strict';

    const uploadRadar = require('./uploadRadar');
    const AsyncIterator = require('./tools/async/AsyncIterator');
    const UploadFail = require('./fails/UploadFail');
    const UploadError = require('./errors/UploadError');
    const UploadLogRecord = require('./UploadLogRecord');

    /**
     * @namespace
     */
    const uploadLog = {
        /**
         * Максимальное количество записей в логе.
         * @type {number}
         */
        RECORDS_LIMIT: 10000,

        /**
         * При превышении лимита на количество записей,
         * они не начинают сразу удаляться по  одной штуке из начала списка,
         * а дожидаются превышения указанного порога, после которого
         * записи удалеяются сразу большой порцией.
         * @type {number}
         */
        RECORDS_THRESHOLD: 1000,

        /**
         * Максимальное количество записей в одном запросе.
         * @type {number}
         */
        RECORDS_GROUP_SIZE: 50,

        /**
         * Время задерки между попытками отправки записей (в миллисекундах).
         * @type {number}
         */
        SEND_DELAY_TIME: 3000,

        /**
         * Разрешает отправку rlog_message.
         * @type {boolean}
         */
        IS_SENDING_ALLOWED: true,

        /**
         * Уровень логирования:
         *     • 0 – только ошибки
         *     • 1 – ошибки и фейлы
         *     • 2 – все записи
         * @type {number}
         */
        LOG_LEVEL: 1,

        /**
         * Автоматическое отображение записей в консоли браузера.
         * @type {boolean}
         */
        debug: false,

        /**
         * @type {Array}
         */
        records: [],

        clear() {
            uploadLog.records.length = 0;
        },

        /**
         * @param {string} type – info, fail or error
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        log(type, data, level) {
            const records = uploadLog.records;
            const record = new UploadLogRecord(type, data, level);
            const recordsCount = records.push(record);

            record.sendCounter();

            if (uploadLog.debug) {
                const message = record.toString();

                switch (type) {
                    case UploadLogRecord.TYPE_ERROR:
                        console.error(message);

                        break;

                    case UploadLogRecord.TYPE_FAIL:
                        console.warn(message);

                        break;

                    case UploadLogRecord.TYPE_INFO:
                        console.log(message);
                }
            }

            const limit = uploadLog.RECORDS_LIMIT;
            const threshold = uploadLog.RECORDS_THRESHOLD;

            if (recordsCount > limit && recordsCount % threshold === 0) {
                // Удаляем часть записей из начала списка,
                // когда лимит превышен на значение threshold.
                records.splice(0, threshold);
            }
        },

        /**
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        info(data, level) {
            uploadLog.log('info', data, level);
        },

        /**
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        fail(data, level) {
            uploadLog.log('fail', data, level);
        },

        /**
         * @param {Object} data
         * @param {number} [level] – overrides log level for important data
         */
        error(data, level) {
            uploadLog.log('error', data, level);
        },

        /**
         * @param {UploadReason} reason
         */
        reason(reason) {
            if (reason instanceof UploadError) {
                uploadLog.error(reason);
            } else if (reason instanceof UploadFail) {
                uploadLog.fail(reason);
            } else {
                uploadLog.info(reason);
            }
        },

        /**
         * Формирует порции неотплавленных записей.
         * @returns {Array.<Array>}
         */
        _getUnsentRecordGroups() {
            const GROUP_SIZE = uploadLog.RECORDS_GROUP_SIZE;
            const LOG_LEVEL = uploadLog.LOG_LEVEL;
            const records = uploadLog.records;
            const groups = [];
            let group = [];

            for (let i = 0, count = records.length; i < count; i++) {
                const record = records[i];

                if (record.isUnsent && record.level <= LOG_LEVEL) {
                    record.isUnsent = false;

                    const groupSize = group.push(record);

                    if (groupSize % GROUP_SIZE === 0) {
                        groups.push(group);
                        group = [];
                    }
                }
            }

            if (group.length) {
                groups.push(group);
            }

            return groups;
        },

        /**
         * @param {UploadLogRecord} record
         * @returns {Object}
         */
        _getRecordDetails(record) {
            return record.getDetails();
        },

        /**
         * Отправляет порцию записей.
         * @param {Array.<UploadLogRecord>} records
         * @param {number} index
         * @param {Array.<Array>} groups
         * @param {Function} next
         */
        _sendRecordsGroup(records, index, groups, next) {
            const data = records.map(uploadLog._getRecordDetails);
            const deferred = uploadRadar.sendLog(data);

            deferred.always(() => {
                next();
            });
        },

        /**
         * Отправляет записи небольшими порциями.
         * @returns {Promise}
         * @resolves {undefined}
         * @rejects {Error}
         */
        sendImmediate() {
            let promise;

            if (uploadLog.IS_SENDING_ALLOWED) {
                const recordGropus = uploadLog._getUnsentRecordGroups();

                promise = new AsyncIterator(
                    recordGropus,
                    uploadLog._sendRecordsGroup,
                );
            } else {
                promise = Promise.reject(new Error('not allowed'));
            }

            return promise;
        },

        /**
         * Отправляет записи небольшими порциями.
         * @type {Function}
         */
        send: null,

        toString() {
            return uploadLog.records.reduce((message, record) => {
                return message + '\n' + record.toString();
            }, '');
        },

        /**
         * Отображает лог в консоли.
         */
        print() {
            const message = uploadLog.toString();

            console.log(message);
        },

        /**
         * Отображает лог в новом окне.
         */
        open() {
            const message = uploadLog.toString();
            const blob = new Blob(['<pre>', message, '</pre>'], {
                type: 'text/html',
            });
            const url = URL.createObjectURL(blob);
            const name = Date.now().toString(36);

            window.open(url, name);
        },
    };

    // TODO: uploadLog.send = uploadLog.sendImmediate.throttle(uploadLog.SEND_DELAY_TIME);
    uploadLog.send = uploadLog.sendImmediate;

    module.exports = uploadLog;
});
